import React from "react";
import PropTypes from "prop-types";
import type {
  ReactDefaultPropsWithChildren,
  ReactComponentStaticProps,
} from "../../types/react";

import { CopyText, CopyTextProps, CopyTextDefaultTagName } from "../CopyText";
import { EqualHeightElement } from "../EqualHeight/EqualHeightElement";

import styles from "./ContentTileNavigation.scss";

export const NavTileCopyDefaultTagName = CopyTextDefaultTagName;
export type NavTileCopyDefaultTag = typeof NavTileCopyDefaultTagName;

type NavTileCopyBaseProps<
  Tag extends React.ElementType = NavTileCopyDefaultTag
> = {
  tag?: Tag;
  variants?: CopyTextProps<Tag>["variants"];
};

export type NavTileCopyProps<
  Tag extends React.ElementType = NavTileCopyDefaultTag
> = NavTileCopyBaseProps<Tag> &
  Omit<React.ComponentPropsWithRef<Tag>, keyof NavTileCopyBaseProps<Tag>>;

const defaultProps = {
  children: "",
  variants: undefined,
  tag: NavTileCopyDefaultTagName,
} as const;

const validatedDefaultProps: ReactDefaultPropsWithChildren<
  NavTileCopyBaseProps,
  typeof defaultProps
> = defaultProps;

const NavTileCopyWithRef = <
  Tag extends React.ElementType = NavTileCopyDefaultTag
>(
  props: NavTileCopyProps<Tag>,
  ref: React.ComponentPropsWithRef<Tag>["ref"]
) => {
  const { children, variants, tag, ...others } = props;
  return (
    <div className={styles.copyWrapper}>
      <EqualHeightElement name="navTileCopy">
        <CopyText
          ref={ref}
          tag={tag as CopyTextProps["tag"]}
          variants={variants}
          {...others}
        >
          {children}
        </CopyText>
      </EqualHeightElement>
    </div>
  );
};

export const NavTileCopy = (React.forwardRef(
  NavTileCopyWithRef
) as unknown) as (<Tag extends React.ElementType = NavTileCopyDefaultTag>(
  props: NavTileCopyProps<Tag>
) => ReturnType<typeof NavTileCopyWithRef>) &
  ReactComponentStaticProps<NavTileCopyProps, "tag">;

NavTileCopy.propTypes = {
  tag: PropTypes.elementType,
  variants: CopyText.propTypes.variants,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

NavTileCopy.defaultProps = validatedDefaultProps;

NavTileCopy.displayName = "ContentTileNavigation.Item.Copy";

export default NavTileCopy;
